import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";


export default function PDFPreviewModal({ open, onClose, pdfData }: { open: boolean; onClose: () => void; pdfData: any }) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!pdfData) {
            setBlobUrl(null);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Convert base64 to blob
            const byteCharacters = atob(pdfData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            // Create object URL
            const url = URL.createObjectURL(blob);
            setBlobUrl(url);
            setLoading(false);
        } catch (err) {
            console.error("Error creating PDF blob:", err);
            setError("Failed to load PDF preview");
            setLoading(false);
        }

        // Cleanup function
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [pdfData]);

    const handleDownload = () => {
        if (!blobUrl) return;
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `prescription_${new Date().getTime()}.pdf`;
        link.click();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                "& .MuiDialog-paper": {
                    height: "90vh",
                },
            }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                PDF Preview
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        {error}
                    </Box>
                ) : blobUrl ? (
                    <iframe
                        src={blobUrl}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                        title="PDF Preview"
                    />
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        No PDF data available
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {blobUrl && (
                    <Button onClick={handleDownload} variant="outlined">
                        Download
                    </Button>
                )}
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
